<template>
  <div
    :class="calendarClasses"
    :style="calendarStyles"
    @dragover.prevent="handleExternalDragOver"
    @dragleave="handleExternalDragLeave"
    @drop.prevent="handleExternalDrop"
  >
    <ejs-schedule
      ref="scheduleObj"
      :height="scheduleHeight"
      :selected-date="initialSelectedDate"
      :current-view="syncfusionView"
      :event-settings="eventSettings"
      :group="group"
      :time-scale="timeScale"
      :work-hours="workHours"
      :enable-adaptive-ui="enableAdaptiveUi"
      :row-auto-height="true"
      :start-hour="'00:00'"
      :end-hour="'24:00'"
      :views="['Day', 'TimelineDay']"
      :toolbar-items="toolbarItems"
      :event-drag-area="eventDragArea"
      :show-quick-info="false"
      :allow-resizing="true"
      :allow-drag-and-drop="true"
      :locale="'pl'"
      @eventClick="handleEventClick"
      @cellClick="handleCellClick"
      @cellDoubleClick="handleCellDoubleClick"
      @actionBegin="handleActionBegin"
      @actionComplete="handleActionComplete"
      @dragStart="handleDragStart"
      @dragStop="handleDragStop"
      @resizeStart="handleResizeStart"
      @resizeStop="handleResizeStop"
      @eventRendered="handleEventRendered"
      @popupOpen="handlePopupOpen"
    >
      <e-resources>
        <e-resource
          field="TeamId"
          title="Zespół"
          name="Teams"
          :data-source="resources"
          text-field="name"
          id-field="id"
        />
      </e-resources>

      <template v-slot:eventTemplate="{ data }">
        <div
          v-if="data.IsBuffer"
          class="order-planner-calendar__buffer-event"
        />
        <div
          v-else-if="isTimelineView && data.IsTimelineComposite"
          class="order-planner-calendar-timeline__composite-event"
        >
          <div
            class="order-planner-calendar-timeline__composite-buffer"
            :style="buildTimelineCompositeBufferStyle(data)"
          />
          <planner-timeline-event-card
            :event-data="buildEventViewModel(data)"
            :style="buildTimelineCompositeCardStyle(data)"
          />
        </div>
        <div
          v-else-if="data.IsTimelineComposite"
          class="order-planner-calendar-scheduler__composite-event"
        >
          <div
            class="order-planner-calendar-scheduler__composite-buffer"
            :style="buildSchedulerCompositeBufferStyle(data)"
          />
          <planner-scheduler-event-card
            :event-data="buildEventViewModel(data)"
            :style="buildSchedulerCompositeCardStyle(data)"
          />
        </div>
        <planner-timeline-event-card
          v-else-if="isTimelineView"
          :event-data="buildEventViewModel(data)"
        />
        <planner-scheduler-event-card
          v-else
          :event-data="buildEventViewModel(data)"
        />
      </template>
    </ejs-schedule>
  </div>
</template>

<script>
import {
  defineComponent, ref, computed, watch, onMounted, onBeforeUnmount,
} from '@vue/composition-api'
import Vue from 'vue'
import moment from 'moment'
import {
  SchedulePlugin,
  Day,
  TimelineViews,
  DragAndDrop,
  Resize,
} from '@syncfusion/ej2-vue-schedule'
import PlannerSchedulerEventCard from './PlannerSchedulerEventCard.vue'
import PlannerTimelineEventCard from './PlannerTimelineEventCard.vue'
import {
  createPlannerSchedulerEventViewModel,
  createTimelineCompositeEvents,
  mapInternalEventToSyncfusion,
  mapSyncfusionEventToInternal,
  resolvePlannerEventColor,
} from '../setup'

Vue.use(SchedulePlugin)

export default defineComponent({
  name: 'OrderPlannerCalendar',
  components: { PlannerSchedulerEventCard, PlannerTimelineEventCard },
  provide: { schedule: [Day, TimelineViews, DragAndDrop, Resize] },
  props: {
    date: { type: String, required: true },
    viewMode: { type: String, default: 'scheduler', validator: v => ['scheduler', 'timeline'].includes(v) },
    showBuffer: { type: Boolean, default: true },
    resources: { type: Array, default: () => [] },
    events: { type: Array, default: () => [] },
    stickyHeaders: { type: Boolean, default: true },
  },
  setup(props, { emit }) {
    const scheduleObj = ref(null)
    const preDragEvent = ref(null)
    const preResizeEvent = ref(null)
    const lastTouchCellClick = ref(null)
    const touchDragEnableFrame = ref(null)

    // Non-reactive: Syncfusion must not see these as changing Vue props.
    const eventsDataSource = []

    const formatPlannerToolbarDate = value => moment(value, 'YYYY-MM-DD')
      .format('D MMM YYYY')
      .replace(/ ([^\s]+) /u, month => month.charAt(0) + month.charAt(1).toUpperCase() + month.slice(2))

    // Non-reactive: initialized once, kept stable so Syncfusion header never rebuilds.
    const initialSelectedDate = new Date(props.date)
    const toolbarItems = [
      {
        align: 'Left', cssClass: 'e-prev', htmlAttributes: { role: 'navigation' }, overflow: 'Show', prefixIcon: 'e-icon-prev', tooltipText: 'Previous',
      },
      {
        align: 'Left', cssClass: 'e-next', htmlAttributes: { role: 'navigation' }, overflow: 'Show', prefixIcon: 'e-icon-next', tooltipText: 'Next',
      },
      {
        align: 'Left',
        cssClass: 'e-date-range',
        htmlAttributes: { 'aria-atomic': 'true', 'aria-live': 'assertive', role: 'navigation' },
        overflow: 'Show',
        suffixIcon: 'e-icon-down-arrow',
        text: formatPlannerToolbarDate(props.date),
      },
      {
        align: 'Right', cssClass: 'e-today', overflow: 'Show', prefixIcon: 'e-icon-today', text: 'Dziś',
      },
    ]

    const calendarClasses = computed(() => {
      const classes = ['order-planner-calendar', `order-planner-calendar--${props.viewMode}`]
      if (props.stickyHeaders) classes.push('order-planner-calendar--sticky-headers')
      return classes
    })

    const calendarStyles = computed(() => {
      if (!props.resources.length) return {}
      return { '--scheduler-grid-min-width': `${props.resources.length * 140}px` }
    })

    const isTimelineView = computed(() => props.viewMode === 'timeline')
    const syncfusionView = computed(() => (isTimelineView.value ? 'TimelineDay' : 'Day'))
    const scheduleHeight = computed(() => (isTimelineView.value ? 'auto' : '100%'))
    const eventDragArea = undefined
    const timeScale = { enable: true, interval: 60, slotCount: 12 }

    const sfEvents = computed(() => props.events.map(mapInternalEventToSyncfusion))
    const computedSfEvents = computed(() => {
      if (!props.showBuffer) return sfEvents.value
      return createTimelineCompositeEvents(sfEvents.value)
    })

    const eventSettings = computed(() => ({
      // References non-reactive eventsDataSource — Vue never sees this computed
      // as having changed, so Syncfusion's Vue binding stays frozen after init.
      dataSource: eventsDataSource,
      template: 'eventTemplate',
    }))

    watch(() => props.date, value => {
      const ej = scheduleObj.value?.ej2Instances
      if (!ej) return
      // Skip if Syncfusion already shows the correct date to avoid a full-panel rebuild.
      // String comparison avoids UTC-vs-local mismatch from parsing ISO date-only strings.
      if (moment(ej.selectedDate).format('YYYY-MM-DD') === value) return
      ej.changeDate(moment(value, 'YYYY-MM-DD').toDate())
    })

    watch(syncfusionView, value => {
      const ej = scheduleObj.value?.ej2Instances
      if (ej) ej.changeView(value)
    })

    watch(computedSfEvents, newEvents => {
      const ej = scheduleObj.value?.ej2Instances
      if (!ej) {
        // Pre-mount: populate in-place so Syncfusion initializes with current events.
        eventsDataSource.splice(0, eventsDataSource.length, ...newEvents)
        return
      }
      // Post-mount: update EJ2 data source directly and call refreshEvents().
      ej.eventSettings.dataSource = newEvents
      ej.refreshEvents()
    }, { immediate: true })

    const resolvePlannerToolbarDateText = () => {
      const selectedDate = scheduleObj.value?.ej2Instances?.selectedDate
      return formatPlannerToolbarDate(selectedDate || props.date)
    }

    const buildEventViewModel = sfData => createPlannerSchedulerEventViewModel({
      ...sfData.Original,
      id: sfData.Id,
      start: sfData.VisualStartTime || sfData.StartTime,
      end: sfData.EndTime,
      resource: sfData.TeamId,
      original: sfData.Original,
    })

    const resolveTimelineCompositeBufferPercent = sfData => {
      const bufferMinutes = Number(sfData?.Original?.bufferBefore || sfData?.BufferBeforeMinutes || 0)
      const durationMinutes = Number(sfData?.Original?.durationMinutes || 0)
      const totalMinutes = bufferMinutes + durationMinutes
      if (bufferMinutes <= 0 || totalMinutes <= 0) return 0
      return Math.min(95, Math.max(0, (bufferMinutes / totalMinutes) * 100))
    }

    const buildTimelineCompositeBufferStyle = sfData => ({
      width: `${resolveTimelineCompositeBufferPercent(sfData)}%`,
    })

    const buildTimelineCompositeCardStyle = sfData => {
      const left = resolveTimelineCompositeBufferPercent(sfData)
      return { left: `${left}%`, width: `${100 - left}%` }
    }

    const buildSchedulerCompositeBufferStyle = sfData => ({
      height: `${resolveTimelineCompositeBufferPercent(sfData)}%`,
    })

    const buildSchedulerCompositeCardStyle = sfData => {
      const top = resolveTimelineCompositeBufferPercent(sfData)
      return { top: `${top}%` }
    }

    const handlePopupOpen = args => {
      // eslint-disable-next-line no-param-reassign
      args.cancel = true
    }

    const handleEventClick = args => {
      if (args.event?.IsBuffer) return
      emit('event-clicked', {
        event: mapSyncfusionEventToInternal(args.event),
        target: args.element || null,
      })
    }

    const createCellClickedPayload = args => {
      const resource = props.resources[args?.groupIndex] || null
      return {
        date: args?.startTime, start: args?.startTime, resource: resource?.id || null, resourceObj: resource,
      }
    }

    const getCellClickKey = args => {
      const date = args?.startTime ? moment(args.startTime).valueOf() : ''
      const resourceId = createCellClickedPayload(args).resource || ''
      return `${resourceId}:${date}`
    }

    const isTouchInteraction = args => {
      const eventType = args?.event?.type || ''
      if (eventType.includes('touch')) return true
      if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
        return window.matchMedia('(pointer: coarse)').matches
      }
      return typeof navigator !== 'undefined' && Number(navigator.maxTouchPoints || 0) > 0
    }

    const enableTouchDragOnAppointments = () => {
      const root = scheduleObj.value?.ej2Instances?.element || document.body
      const appointments = root?.querySelectorAll?.('.e-appointment.e-draggable') || []
      appointments.forEach(element => {
        const draggable = (element.ej2_instances || [])
          .find(instance => instance.getModuleName?.() === 'draggable')
        if (!draggable || draggable.enableTapHold) return
        if (typeof draggable.toggleEvents === 'function') draggable.toggleEvents(true)
        draggable.enableTapHold = true
        draggable.tapHoldThreshold = 350
        if (typeof draggable.toggleEvents === 'function') draggable.toggleEvents(false)
      })
    }

    const scheduleTouchDragEnable = () => {
      if (touchDragEnableFrame.value) cancelAnimationFrame(touchDragEnableFrame.value)
      touchDragEnableFrame.value = requestAnimationFrame(() => {
        touchDragEnableFrame.value = null
        enableTouchDragOnAppointments()
      })
    }

    const handleCellClick = args => {
      if (!isTouchInteraction(args)) return
      // eslint-disable-next-line no-param-reassign
      args.cancel = true
      const key = getCellClickKey(args)
      const clickedAt = Date.now()
      const previousClick = lastTouchCellClick.value
      lastTouchCellClick.value = { key, clickedAt }
      if (!previousClick || previousClick.key !== key || clickedAt - previousClick.clickedAt > 450) return
      lastTouchCellClick.value = null
      emit('cell-double-clicked', createCellClickedPayload(args))
    }

    const handleEventRendered = args => {
      const element = args?.element
      if (!element?.classList) return
      if (args.data?.IsBuffer) element.classList.add('order-planner-calendar-timeline__buffer-host')
      scheduleTouchDragEnable()
    }

    const handleCellDoubleClick = args => {
      // eslint-disable-next-line no-param-reassign
      args.cancel = true
      lastTouchCellClick.value = null
      emit('cell-double-clicked', createCellClickedPayload(args))
    }

    const isPlannerHiddenToolbarItem = item => {
      const cssClass = item.cssClass || ''
      return cssClass.includes('e-add') || cssClass.includes('e-views') || cssClass.includes('e-schedule-seperator')
    }

    const normalizePlannerToolbarItem = item => {
      const normalizedItem = { ...item }
      const cssClass = normalizedItem.cssClass || ''
      if (cssClass.includes('e-date-range')) {
        normalizedItem.text = resolvePlannerToolbarDateText()
        normalizedItem.overflow = 'Show'
        normalizedItem.showAlwaysInPopup = false
      }
      if (cssClass.includes('e-today')) {
        normalizedItem.align = 'Right'
        normalizedItem.text = 'Dziś'
        normalizedItem.overflow = 'Show'
        normalizedItem.showAlwaysInPopup = false
      }
      return normalizedItem
    }

    const handleActionBegin = args => {
      if (args.requestType !== 'toolbarItemRendering' || !Array.isArray(args.items)) return
      // Syncfusion switches toolbar composition on touch/iPad devices even with
      // adaptive UI disabled. Keep planner navigation identical across inputs.
      // eslint-disable-next-line no-param-reassign
      args.items = args.items
        .filter(item => !isPlannerHiddenToolbarItem(item))
        .map(item => normalizePlannerToolbarItem(item))
    }

    const handleActionComplete = args => {
      if (args.requestType === 'dateNavigate') {
        const nextDate = moment(scheduleObj.value?.ej2Instances?.selectedDate).format('YYYY-MM-DD')
        emit('selected-date-change', nextDate)
      }
      scheduleTouchDragEnable()
    }

    const handleDragStart = args => {
      if (!isTimelineView.value) {
        // eslint-disable-next-line no-param-reassign
        args.interval = 5
      }
      preDragEvent.value = args.data ? { ...args.data } : null
    }

    const handleDragStop = args => {
      const sfEvent = args.data
      if (!sfEvent || sfEvent.IsBuffer || sfEvent.IsReadonly) {
        // eslint-disable-next-line no-param-reassign
        args.cancel = true
        return
      }
      const oldSfEvent = preDragEvent.value
      preDragEvent.value = null
      emit('event-updated', {
        event: mapSyncfusionEventToInternal(sfEvent),
        oldEvent: oldSfEvent ? mapSyncfusionEventToInternal(oldSfEvent) : null,
      })
    }

    const handleResizeStart = args => {
      if (!args.data?.AllowResize || args.data?.IsBuffer) {
        // eslint-disable-next-line no-param-reassign
        args.cancel = true
        return
      }
      if (!isTimelineView.value) {
        // eslint-disable-next-line no-param-reassign
        args.interval = 5
      }
      preResizeEvent.value = args.data ? { ...args.data } : null
    }

    const handleResizeStop = args => {
      const sfEvent = args.data
      if (!sfEvent || sfEvent.IsBuffer) return
      const oldSfEvent = preResizeEvent.value
      preResizeEvent.value = null
      emit('event-updated', {
        event: mapSyncfusionEventToInternal(sfEvent),
        oldEvent: oldSfEvent ? mapSyncfusionEventToInternal(oldSfEvent) : null,
      })
    }

    const dragHoverCell = ref(null)

    const clearDragHover = () => {
      if (dragHoverCell.value) {
        dragHoverCell.value.classList.remove('e-planner-drag-hover')
        dragHoverCell.value = null
      }
    }

    const handleExternalDragOver = nativeEvent => {
      const cell = nativeEvent.target.closest('.e-work-cells')
      if (cell === dragHoverCell.value) return
      clearDragHover()
      if (cell) {
        cell.classList.add('e-planner-drag-hover')
        dragHoverCell.value = cell
      }
    }

    const handleExternalDragLeave = nativeEvent => {
      if (!nativeEvent.relatedTarget || !nativeEvent.currentTarget.contains(nativeEvent.relatedTarget)) {
        clearDragHover()
      }
    }

    const handleExternalDrop = nativeEvent => {
      clearDragHover()
      try {
        const raw = nativeEvent.dataTransfer.getData('text/plain')
        if (!raw) return
        const order = JSON.parse(raw)
        if (!order) return
        const scheduleObjRef = scheduleObj.value
        if (!scheduleObjRef) return
        const cellDetails = scheduleObjRef.getCellDetails(nativeEvent.target)
        if (!cellDetails) return
        const resource = props.resources[cellDetails.groupIndex] || null
        if (!resource) return
        emit('event-created', {
          original: { ...(order.original || order), orderId: order.original?.orderId || order.id },
          resource: resource.id,
          start: moment(cellDetails.startTime).format('YYYY-MM-DDTHH:mm:ss'),
          end: moment(cellDetails.endTime).format('YYYY-MM-DDTHH:mm:ss'),
          color: resolvePlannerEventColor((order.original || order).type),
        })
      } catch (e) {
        // ignore invalid drops
      }
    }

    const navigateBy = offset => {
      const ej = scheduleObj.value?.ej2Instances
      if (ej) ej.changeDate(moment(props.date).add(offset, 'day').toDate())
    }

    const navigateTo = value => {
      const ej = scheduleObj.value?.ej2Instances
      if (ej) ej.changeDate(moment(value, 'YYYY-MM-DD').toDate())
    }

    onMounted(() => { scheduleTouchDragEnable() })

    onBeforeUnmount(() => {
      if (touchDragEnableFrame.value) cancelAnimationFrame(touchDragEnableFrame.value)
    })

    return {
      scheduleObj,
      preDragEvent,
      preResizeEvent,
      calendarClasses,
      calendarStyles,
      isTimelineView,
      syncfusionView,
      scheduleHeight,
      eventDragArea,
      eventSettings,
      sfEvents,
      computedSfEvents,
      initialSelectedDate,
      toolbarItems,
      enableAdaptiveUi: false,
      group: { resources: ['Teams'], enableCompactView: false },
      timeScale,
      workHours: { highlight: false },
      buildEventViewModel,
      buildTimelineCompositeBufferStyle,
      buildTimelineCompositeCardStyle,
      buildSchedulerCompositeBufferStyle,
      buildSchedulerCompositeCardStyle,
      enableTouchDragOnAppointments,
      handleEventClick,
      handleCellClick,
      handleCellDoubleClick,
      handleActionBegin,
      handleActionComplete,
      handleDragStart,
      handleDragStop,
      handleResizeStart,
      handleResizeStop,
      handleExternalDragOver,
      handleExternalDragLeave,
      handleExternalDrop,
      handleEventRendered,
      handlePopupOpen,
      navigateBy,
      navigateTo,
    }
  },
})
</script>

<style lang="scss">
@import './order-planner-calendar-scheduler';
@import './order-planner-calendar-timeline';

// Syncfusion shows its own loading spinner when the event data source is replaced.
// We manage loading state ourselves, so suppress this overlay to prevent visual flicker.
.order-planner-calendar .e-spinner-pane {
  display: none !important;
}
</style>
